import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FormInput from "../components/FormInput";
import PasswordFormField from "../components/PasswordFormField";
import PrimaryButton from "../components/PrimaryButton";
import "../styles/auth.css";

const AUTH_TOKEN_STORAGE_KEY = "authToken";
const AUTH_ROLE_STORAGE_KEY = "authRole";
const AUTH_NAME_STORAGE_KEY = "authName";

const normalizeRole = (role) => {
  const cleaned = String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, "_");

  if (cleaned === "SUPERADMIN") {
    return "SUPER_ADMIN";
  }
  if (cleaned === "TANNERYADMIN") {
    return "TANNERY_ADMIN";
  }

  return cleaned;
};

const getBaseApiUrl = () => {
  const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
  if (!baseApiUrl) {
    throw new Error("REACT_APP_BASE_API_URL is not configured.");
  }
  return baseApiUrl.replace(/\/+$/, "");
};

const formatLoginError = (statusCode, errorPayload, fallbackMessage) => {
  if (!errorPayload) {
    return fallbackMessage;
  }

  if (typeof errorPayload === "string") {
    return errorPayload;
  }

  if (errorPayload?.errors && typeof errorPayload.errors === "object") {
    const fieldErrors = Object.values(errorPayload.errors)
      .filter(Boolean)
      .join(" ");

    if (fieldErrors) {
      return fieldErrors;
    }
  }

  if (typeof errorPayload?.message === "string" && errorPayload.message.trim()) {
    if (statusCode === 401) {
      return "Invalid username or password.";
    }
    return errorPayload.message;
  }

  return fallbackMessage;
};

const extractBearerToken = (payload) => {
  if (!payload || typeof payload !== "object") {
    return "";
  }

  return (
    payload.access_token ||
    payload.token ||
    payload.bearer_token ||
    payload?.data?.access_token ||
    payload?.data?.token ||
    ""
  );
};

const extractUserRole = (payload) => {
  if (!payload || typeof payload !== "object") {
    return "";
  }

  return (
    payload.role ||
    payload.user_role ||
    payload?.roles?.[0] ||
    payload?.user?.roles?.[0] ||
    payload?.user?.role ||
    payload?.data?.role ||
    payload?.data?.roles?.[0] ||
    payload?.data?.user?.role ||
    payload?.data?.user?.roles?.[0] ||
    ""
  );
};

const extractUserName = (payload) => {
  if (!payload || typeof payload !== "object") {
    return "";
  }

  return (
    payload.name ||
    payload.full_name ||
    payload.username ||
    payload?.user?.name ||
    payload?.user?.full_name ||
    payload?.user?.username ||
    payload?.data?.name ||
    payload?.data?.full_name ||
    payload?.data?.username ||
    ""
  );
};

const extractRoleFromToken = (token) => {
  try {
    const tokenParts = token.split(".");
    if (tokenParts.length < 2) {
      return "";
    }

    const base64Url = tokenParts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const paddedBase64 = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const tokenPayload = JSON.parse(atob(paddedBase64));

    return (
      tokenPayload.role ||
      tokenPayload.user_role ||
      tokenPayload?.roles?.[0] ||
      tokenPayload?.authorities?.[0] ||
      ""
    );
  } catch (error) {
    return "";
  }
};

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [logoutMessage, setLogoutMessage] = useState(location.state?.logoutMessage || "");
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const stateMessage = location.state?.logoutMessage || "";
    if (!stateMessage) {
      return;
    }

    setLogoutMessage(stateMessage);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!formData.username.trim()) {
      nextErrors.username = "Please enter your username.";
    }

    if (!formData.password.trim()) {
      nextErrors.password = "Please enter your password.";
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validate();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      const baseApiUrl = getBaseApiUrl();
      const loginUrl = `${baseApiUrl}/auth/login`;
      console.log("Login request URL:", loginUrl);

      const response = await fetch(loginUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorPayload;

        try {
          errorPayload = JSON.parse(errorText);
        } catch (parseError) {
          errorPayload = errorText;
        }

        const fallbackMessage =
          response.status === 401
            ? "Invalid username or password."
            : "Unable to login. Please try again.";

        throw new Error(
          formatLoginError(response.status, errorPayload, fallbackMessage)
        );
      }

      const responsePayload = await response.json();
      const bearerToken = extractBearerToken(responsePayload);
      if (!bearerToken) {
        throw new Error("Login succeeded but token was not returned by server.");
      }
      const userRole = normalizeRole(
        extractUserRole(responsePayload) || extractRoleFromToken(bearerToken)
      );
      const userName = extractUserName(responsePayload) || formData.username.trim();

      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, bearerToken);
      localStorage.setItem(AUTH_ROLE_STORAGE_KEY, userRole);
      localStorage.setItem(AUTH_NAME_STORAGE_KEY, userName);
      navigate("/dashboard");
    } catch (error) {
      console.error("Login request failed:", error);
      setErrors((prev) => ({
        ...prev,
        form: error.message || "Unable to login. Please try again.",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="auth-page">
      <section className="auth-card">
        <img
          className="auth-logo"
          src="/alkhidmat-logo.png"
          alt="Alkhidmat logo"
          width="120"
          height="120"
        />
        <h1>Alkhidmat Tannery Management</h1>
        <p className="subtitle">Portal Login</p>
        {logoutMessage ? (
          <p className="form-success" role="status">
            {logoutMessage}
          </p>
        ) : null}

        <form onSubmit={handleSubmit} noValidate>
          <FormInput
            id="username"
            name="username"
            label="Username"
            value={formData.username}
            onChange={handleChange}
            placeholder="Enter username"
            error={errors.username}
            required
          />

          <PasswordFormField
            id="password"
            name="password"
            label="Password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter password"
            error={errors.password}
            required
            autoComplete="current-password"
          />

          {errors.form ? <p className="form-error">{errors.form}</p> : null}

          <PrimaryButton type="submit" fullWidth>
            {isSubmitting ? "Logging in..." : "Login"}
          </PrimaryButton>
        </form>
      </section>
    </main>
  );
}

export default LoginPage;
